import React from 'react';
import { WheelSafeNumberInput } from '../common/WheelSafeNumberInput';
import {
  Edit,
  SimpleForm,
  TextInput,
  BooleanInput,
  SelectInput,
  useTranslate,
  useRecordContext,
  Toolbar,
  SaveButton,
  ReferenceInput,
  FormDataConsumer,
  minValue,
  useRedirect,
  useNotify,
} from 'react-admin';
import { ServerErrorBinder } from '../common/ServerErrorBinder';
import { translateServerMessage } from '../../utils/translateServerMessage';

const PAYMENT_METHOD_KNOWN_FIELDS = new Set([
    'name', 'code', 'is_active', 'sort_order', 'icon_key', 'gateway_id',
    'translations', 'description', 'fee_percentage', 'fee_fixed', 'currency',
    'supported_currencies', 'min_amount', 'max_amount',
]);

const mapPaymentMethodBackendPath = (path: string): string =>
    path.replace(/^body\./, '').replace(/^params\./, '').replace(/^query\./, '');

const paymentMethodFieldLabelKey = (field: string): string => {
    const head = field.split('.')[0];
    return PAYMENT_METHOD_KNOWN_FIELDS.has(head)
        ? `resources.payment-methods.fields.${head}`
        : field;
};
import {
  Box,
  Card,
  CardContent,
  Typography,
  IconButton,
  Chip,
} from '@mui/material';
import {
  ArrowBack,
  CreditCard,
  AccountBalanceWallet,
  PhoneIphone,
  Settings,
  Translate,
  Info,
} from '@mui/icons-material';

const EditToolbar = () => {
  const translate = useTranslate();

  return (
    <Toolbar
      sx={{
        backgroundColor: '#FAFAFA',
        borderTop: '1px solid #E0E0E0',
        px: { xs: 2, sm: 3 },
        py: 2,
        mt: { xs: 2, sm: 4 },
        '& .MuiButton-root': {
          borderRadius: 2,
          textTransform: 'none',
          fontWeight: 600,
          px: 3,
          py: 1.5,
        },
      }}
    >
      <SaveButton
        variant="contained"
        sx={{
          backgroundColor: '#00C853',
          '&:hover': {
            backgroundColor: '#00A843',
            boxShadow: '0 4px 12px rgba(0,200,83,0.3)',
          },
        }}
      />
    </Toolbar>
  );
};

const PageHeader = () => {
  const redirect = useRedirect();
  const translate = useTranslate();
  const record = useRecordContext();

  if (!record) return null;

  const getIcon = () => {
    switch (record.type) {
      case 'card':
        return <CreditCard sx={{ fontSize: { xs: 24, sm: 32 }, color: '#00C853' }} />;
      case 'ewallet':
        return <AccountBalanceWallet sx={{ fontSize: { xs: 24, sm: 32 }, color: '#00C853' }} />;
      case 'mobile_wallet':
        return <PhoneIphone sx={{ fontSize: { xs: 24, sm: 32 }, color: '#00C853' }} />;
      default:
        return <CreditCard sx={{ fontSize: { xs: 24, sm: 32 }, color: '#00C853' }} />;
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        mb: { xs: 2, sm: 4 },
      }}
    >
      <IconButton
        onClick={() => redirect('/payment-methods')}
        size="large"
        sx={{
          backgroundColor: '#FFFFFF',
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          '&:hover': {
            backgroundColor: '#F5F5F5',
            boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
          },
        }}
      >
        <ArrowBack />
      </IconButton>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
        {getIcon()}
        <Box>
          <Typography sx={{ fontWeight: 600, fontSize: { xs: '1.25rem', sm: '2.125rem' }, wordBreak: 'break-word', overflowWrap: 'anywhere', mb: 0.5 }}>
            {record.name}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {translate('resources.payment-methods.hints.edit_info')}
          </Typography>
        </Box>
      </Box>
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        <Chip
          label={translate(`resources.payment-methods.types.${record.type}`)}
          color="primary"
          variant="outlined"
          size="small"
        />
        {record.supports_tokenization && (
          <Chip
            label={translate('resources.payment-methods.fields.supports_tokenization')}
            color="info"
            size="small"
          />
        )}
      </Box>
    </Box>
  );
};

export const PaymentMethodsEdit = () => {
  const translate = useTranslate();
  const notify = useNotify();
  const redirect = useRedirect();
  const [serverErrors, setServerErrors] = React.useState<Record<string, string>>({});

  return (
    <Edit
      title={false}
      component="div"
      redirect="list"
      mutationMode="pessimistic"
      actions={false}
      mutationOptions={{
        onSuccess: () => {
          setServerErrors({});
          notify('ra.notification.updated', { type: 'info', messageArgs: { smart_count: 1 } });
          redirect('list', 'payment-methods');
        },
        onError: (error: any) => {
          const apiError = error?.body || error;
          const rawErrors = apiError?.errors as Record<string, string[] | string> | undefined;
          const fieldErrors: Record<string, string> = {};
          const summaryLines: string[] = [];
          if (rawErrors) {
            for (const [path, msgs] of Object.entries(rawErrors)) {
              const rawMsg = Array.isArray(msgs) ? msgs[0] : String(msgs);
              const translated = translateServerMessage(rawMsg, translate);
              const field = mapPaymentMethodBackendPath(path);
              fieldErrors[field] = translated;
              const label = translate(paymentMethodFieldLabelKey(field), { _: field });
              summaryLines.push(`• ${label}: ${translated}`);
            }
          }
          setServerErrors(fieldErrors);
          const headerKey = apiError?.errorCode ? `errors.${apiError.errorCode}` : 'validation.formHasErrors';
          const header = translate(headerKey, { _: apiError?.message || 'Error' });
          if (summaryLines.length > 0) {
            notify(`${header}\n${summaryLines.join('\n')}`, { type: 'error', multiLine: true, autoHideDuration: 10000 });
          } else {
            notify(header, { type: 'error', autoHideDuration: 6000 });
          }
        },
      }}
      sx={{
        backgroundColor: 'transparent',
        '& .RaEdit-main': {
          backgroundColor: 'transparent',
          boxShadow: 'none',
        },
      }}
    >
      <SimpleForm
        toolbar={<EditToolbar />}
        sx={{
          '& .MuiPaper-root': {
            backgroundColor: 'transparent',
            boxShadow: 'none',
          },
          '& > .MuiCardContent-root, & > div > .MuiCardContent-root': {
            px: { xs: 0, sm: 2 },
            py: { xs: 1, sm: 2 },
          },
          padding: { xs: 0, sm: 'inherit' },
        }}
      >
        <ServerErrorBinder errors={serverErrors} />
        <Box sx={{ width: '100%', maxWidth: 800, margin: '0 auto', '& .MuiFormControl-root': { width: '100%' }, pb: { xs: 10, sm: 2 } }}>
          <PageHeader />

          <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Settings sx={{ mr: 2, color: '#00C853' }} />
                <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                  {translate('resources.payment-methods.sections.basic_settings')}
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                  <Box sx={{ flex: 1, minWidth: 0, '& .MuiFormControl-root': { marginTop: 0, width: '100%' } }}>
                    <BooleanInput
                      source="is_active"
                      label={translate('resources.payment-methods.fields.is_active')}
                      helperText={translate('resources.payment-methods.hints.is_active')}
                    />
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, alignItems: { xs: 'stretch', sm: 'flex-start' }, '& .MuiFormControl-root': { marginTop: 0 } }}>
                  <Box sx={{ flex: 1, minWidth: 0, '& .MuiFormControl-root': { marginTop: 0, width: '100%' } }}>
                    <WheelSafeNumberInput
                      source="sort_order"
                      label={translate('resources.payment-methods.fields.sort_order')}
                      validate={[minValue(0)]}
                      helperText={translate('resources.payment-methods.hints.sort_order')}
                      fullWidth
                    />
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0, '& .MuiFormControl-root': { marginTop: 0, width: '100%' } }}>
                    <TextInput
                      source="icon_key"
                      label={translate('resources.payment-methods.fields.icon_key')}
                      helperText={translate('resources.payment-methods.hints.icon_key')}
                      fullWidth
                    />
                  </Box>
                </Box>
              </Box>
            </CardContent>
          </Card>

          <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <AccountBalanceWallet sx={{ mr: 2, color: '#00C853' }} />
                <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                  {translate('resources.payment-methods.sections.gateway_settings')}
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box sx={{ '& .MuiFormControl-root': { marginTop: 0, width: '100%' } }}>
                  <ReferenceInput
                    source="gateway_id"
                    reference="payment-gateways"
                    sort={{ field: 'name', order: 'ASC' }}
                  >
                    <SelectInput
                      optionText="name"
                      label={translate('resources.payment-methods.fields.gateway')}
                      helperText={translate('resources.payment-methods.hints.gateway')}
                      fullWidth
                    />
                  </ReferenceInput>
                </Box>

                <FormDataConsumer>
                  {({ formData }) => (
                    <Box
                      sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: { xs: 2, sm: 3 },
                        mt: 1,
                        p: 2,
                        backgroundColor: '#F5F5F5',
                        borderRadius: 1,
                      }}
                    >
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          {translate('resources.payment-methods.fields.gateway_method_code')}
                        </Typography>
                        <Typography variant="body2" fontWeight={500}>
                          {formData?.gateway_method_code || '-'}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          {translate('resources.payment-methods.fields.code')}
                        </Typography>
                        <Typography variant="body2" fontWeight={500}>
                          {formData?.code || '-'}
                        </Typography>
                      </Box>
                    </Box>
                  )}
                </FormDataConsumer>
              </Box>
            </CardContent>
          </Card>

          <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Translate sx={{ mr: 2, color: '#00C853' }} />
                <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                  {translate('resources.payment-methods.sections.translations')}
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                    English (en)
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, alignItems: { xs: 'stretch', sm: 'flex-start' }, '& .MuiFormControl-root': { marginTop: 0 } }}>
                    <Box sx={{ flex: 1, minWidth: 0, '& .MuiFormControl-root': { marginTop: 0, width: '100%' } }}>
                      <TextInput
                        source="translations.en.display_name"
                        label={translate('resources.payment-methods.fields.display_name')}
                        fullWidth
                      />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0, '& .MuiFormControl-root': { marginTop: 0, width: '100%' } }}>
                      <TextInput
                        source="translations.en.description"
                        label={translate('resources.payment-methods.fields.description')}
                        fullWidth
                      />
                    </Box>
                  </Box>
                </Box>

                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                    繁體中文 (zh_TW)
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, alignItems: { xs: 'stretch', sm: 'flex-start' }, '& .MuiFormControl-root': { marginTop: 0 } }}>
                    <Box sx={{ flex: 1, minWidth: 0, '& .MuiFormControl-root': { marginTop: 0, width: '100%' } }}>
                      <TextInput
                        source="translations.zh_TW.display_name"
                        label={translate('resources.payment-methods.fields.display_name')}
                        fullWidth
                      />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0, '& .MuiFormControl-root': { marginTop: 0, width: '100%' } }}>
                      <TextInput
                        source="translations.zh_TW.description"
                        label={translate('resources.payment-methods.fields.description')}
                        fullWidth
                      />
                    </Box>
                  </Box>
                </Box>

                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                    简体中文 (zh_CN)
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, alignItems: { xs: 'stretch', sm: 'flex-start' }, '& .MuiFormControl-root': { marginTop: 0 } }}>
                    <Box sx={{ flex: 1, minWidth: 0, '& .MuiFormControl-root': { marginTop: 0, width: '100%' } }}>
                      <TextInput
                        source="translations.zh_CN.display_name"
                        label={translate('resources.payment-methods.fields.display_name')}
                        fullWidth
                      />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0, '& .MuiFormControl-root': { marginTop: 0, width: '100%' } }}>
                      <TextInput
                        source="translations.zh_CN.description"
                        label={translate('resources.payment-methods.fields.description')}
                        fullWidth
                      />
                    </Box>
                  </Box>
                </Box>
              </Box>
            </CardContent>
          </Card>

          <Card sx={{ backgroundColor: '#FAFAFA', overflow: 'hidden' }}>
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Info sx={{ mr: 2, color: '#757575' }} />
                <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' }, color: '#757575' }}>
                  {translate('resources.payment-methods.sections.read_only')}
                </Typography>
              </Box>

              <FormDataConsumer>
                {({ formData }) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: { xs: 2, sm: 3 } }}>
                    <Box sx={{ minWidth: 120 }}>
                      <Typography variant="caption" color="text.secondary">
                        {translate('resources.payment-methods.fields.type')}
                      </Typography>
                      <Typography variant="body2" fontWeight={500} sx={{ textTransform: 'capitalize' }}>
                        {formData?.type ? translate(`resources.payment-methods.types.${formData.type}`) : '-'}
                      </Typography>
                    </Box>
                    <Box sx={{ minWidth: 120 }}>
                      <Typography variant="caption" color="text.secondary">
                        {translate('resources.payment-methods.fields.device_type')}
                      </Typography>
                      <Typography variant="body2" fontWeight={500}>
                        {formData?.device_type
                          ? translate(`resources.payment-methods.device_types.${formData.device_type}`)
                          : '-'}
                      </Typography>
                    </Box>
                    <Box sx={{ minWidth: 120 }}>
                      <Typography variant="caption" color="text.secondary">
                        {translate('resources.payment-methods.fields.supports_tokenization')}
                      </Typography>
                      <Typography variant="body2" fontWeight={500}>
                        {formData?.supports_tokenization ? translate('common.yes') : translate('common.no')}
                      </Typography>
                    </Box>
                  </Box>
                )}
              </FormDataConsumer>
            </CardContent>
          </Card>
        </Box>
      </SimpleForm>
    </Edit>
  );
};
